import { useState, useCallback } from 'react';
import { Panel } from '@/components/Panel';
import { CanvasChart } from '@/components/CanvasChart';
import { useControlCharts } from '@/hooks/useControlCharts';
import { Spinner, EmptyState } from '@00start/ui';
import { SIGNAL, SIGMA_BANDS, drawGridLines, formatTime } from '@/utils/chart-helpers';

const PAD = { top: 12, right: 12, bottom: 28, left: 42 };

const METRIC_OPTIONS = [
  { value: 'latency_p95', label: 'Latency p95' },
  { value: 'error_rate', label: 'Error Rate' },
  { value: 'queue_depth', label: 'Queue Depth' },
];

export function ControlChart() {
  const [metric, setMetric] = useState('latency_p95');
  const { data, loading } = useControlCharts(metric);

  const onDraw = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) => {
      if (!data || !data.points.length) return;

      const { mean, stddev, points } = data;

      // 1. Compute Y range with minimum visible range
      const range = Math.max(stddev * 4, 0.5);
      const yMin = mean - range;
      const yMax = mean + range;

      // 2. Draw Y-axis grid lines
      drawGridLines(ctx, w, h, PAD, yMin, yMax, 5);

      const plotW = w - PAD.left - PAD.right;
      const plotH = h - PAD.top - PAD.bottom;

      function toY(v: number): number {
        return PAD.top + plotH - ((v - yMin) / (yMax - yMin)) * plotH;
      }

      // 3. Sigma bands: outermost first (3σ), then 2σ, then 1σ
      const bands: [number, string][] = [
        [3, SIGMA_BANDS.three],
        [2, SIGMA_BANDS.two],
        [1, SIGMA_BANDS.one],
      ];

      for (const [sigma, color] of bands) {
        const bandTop = toY(mean + sigma * stddev);
        const bandBot = toY(mean - sigma * stddev);
        ctx.fillStyle = color;
        ctx.fillRect(PAD.left, bandTop, plotW, bandBot - bandTop);
      }

      // 4. Mean line: dashed greyscale
      const meanY = toY(mean);
      ctx.setLineDash([6, 4]);
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(148,163,184,0.5)';
      ctx.beginPath();
      ctx.moveTo(PAD.left, meanY);
      ctx.lineTo(w - PAD.right, meanY);
      ctx.stroke();
      ctx.setLineDash([]);

      // 5. Data line: solid greyscale
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = 'rgba(148,163,184,0.7)';
      ctx.beginPath();
      points.forEach((pt, i) => {
        const x = PAD.left + (i / (points.length - 1 || 1)) * plotW;
        const y = toY(pt.value);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();

      // 6. Violation dots and X axis labels
      points.forEach((pt, i) => {
        const x = PAD.left + (i / (points.length - 1 || 1)) * plotW;
        const y = toY(pt.value);

        if (pt.rule_violations.length > 0) {
          ctx.beginPath();
          ctx.arc(x, y, 4, 0, Math.PI * 2);
          ctx.fillStyle = pt.sigma_level >= 3 ? SIGNAL.critical : SIGNAL.warning;
          ctx.fill();
        }

        // 7. X axis time labels every ~6th point
        if (i % 6 === 0) {
          ctx.fillStyle = 'rgba(148,163,184,0.5)';
          ctx.font = '10px SF Mono, monospace';
          ctx.textAlign = 'center';
          ctx.fillText(formatTime(pt.timestamp), x, h - PAD.bottom + 14);
        }
      });
    },
    [data],
  );

  const headerAction = (
    <select
      data-testid="ops-control-chart-metric-select"
      value={metric}
      onChange={(e) => setMetric(e.target.value)}
      className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-[10px] text-slate-300"
    >
      {METRIC_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  );

  return (
    <Panel title="Control Chart" wide mobilePriority={6} headerAction={headerAction}>
      {loading ? (
        <div className="flex justify-center py-4">
          <Spinner size="sm" />
        </div>
      ) : !data || !data.points.length ? (
        <EmptyState title="No control chart data" description="No data available for the selected metric." />
      ) : (
        <CanvasChart onDraw={onDraw} height={220} />
      )}
    </Panel>
  );
}
